/**
 * cashflowChartData.ts – turns the projection engine's per-year rows into the points the
 * projections modal's cashflow chart draws: stacked income categories, a total expenses level,
 * and the shortfall between them.
 *
 * Income comes from each year's `cashFlowBreakdown` (the numeric label/value list
 * `calculateFinancials` returns): positive lines are income, negative lines are expenses. Aged
 * care sits outside that breakdown unless the strategy integrates it, so it is appended from
 * `annualCareCost` when no expense line already covers it — same reconciliation the modal's
 * "Net Cashflow" tooltip does. Rows without a breakdown fall back to the row's scalar fields.
 */
import { CHART_CATEGORICAL_SLOTS, CHART_STATUS_CRITICAL, type ChartPoint, type ChartSeries, type ChartValueLine } from '../../components/charts/chartTypes.ts';

/** A stacked income category. Ids are stable — the chart's legend toggles key off them. */
export type CashflowSeriesId = 'salary' | 'pension' | 'investment' | 'abp' | 'other';

/**
 * The income stack, bottom-up. These five slots were picked (and validated) before the palette was
 * shared across the accordion's three charts, so they are named individually rather than taken in
 * slot order — repainting an existing series would be a change of identity, not of styling.
 */
export const CASHFLOW_INCOME_SERIES: readonly ChartSeries[] = [
    { id: 'salary', label: 'Salary', color: CHART_CATEGORICAL_SLOTS[0] },
    { id: 'pension', label: 'Age Pension', color: CHART_CATEGORICAL_SLOTS[2] },
    { id: 'investment', label: 'Investment income', color: CHART_CATEGORICAL_SLOTS[3] },
    { id: 'abp', label: 'ABP income', color: CHART_CATEGORICAL_SLOTS[6] },
    { id: 'other', label: 'Other income', color: CHART_CATEGORICAL_SLOTS[4] },
];

/** Status "critical" — a state (expenses not covered by income), deliberately not a categorical slot. */
export const CASHFLOW_SHORTFALL_COLOR = CHART_STATUS_CRITICAL;

/** The subset of a projection row this module reads. Kept structural so the modal's own richer type fits. */
interface ProjectionRowLike {
    fyLabel?: string;
    socialSecurity?: number;
    /** Pension actually deposited across the period — preferred over the opening rate above. */
    agePensionPaidForPeriod?: number;
    investmentIncome?: number;
    abpIncome?: number;
    salaryIncome?: number;
    rentalIncome?: number;
    annualCareCost?: number;
    livingExpenses?: number;
    tax?: number;
    capitalExpenses?: number;
    cashFlowBreakdown?: unknown;
    [key: string]: unknown;
}

const EMPTY_INCOME = (): Record<CashflowSeriesId, number> => ({ salary: 0, pension: 0, investment: 0, abp: 0, other: 0 });

/**
 * Route a positive `cashFlowBreakdown` line to an income series. Salary is split out separately.
 *
 * READS THE ENGINE'S `series` TAG FIRST, and that is not decoration. Investment income used to
 * arrive as one line labelled "Investment Income (Cash)", matched here on the substring "investment
 * income". When it was split into Interest / Dividends / Rent so a client could tell which asset
 * produced what (2026-08-22), three of those four labels no longer contained that substring — so
 * without the tag, interest, dividends and rent would all have silently landed in the "other" band
 * of every cashflow chart. The chart would still add up; it would just attribute the money to the
 * wrong thing, which is worse than an error.
 *
 * The label checks remain for lines the engine has not tagged, and for stored projections written
 * before the tag existed.
 */
function seriesForIncomeLabel(label: string, series?: string): CashflowSeriesId {
    if (series === 'investment' || series === 'abp' || series === 'pension' || series === 'salary' || series === 'other') {
        return series as CashflowSeriesId;
    }
    const l = label.toLowerCase();
    if (l.includes('abp') || l.includes('account based pension') || l.includes('account-based pension')) return 'abp';
    // Before 'rent': Rent Assistance is an Age Pension supplement, not investment rent.
    if (l.includes('age pension') || l.includes('rent assistance')) return 'pension';
    if (l.includes('investment income') || l.startsWith('interest') || l.startsWith('dividends') || l.startsWith('rent (')) return 'investment';
    return 'other';
}

/** Strip the breakdown's `Less: ` / `Add: ` prefixes so tooltip rows read as plain expense names. */
function cleanExpenseLabel(label: string): string {
    return label.replace(/^\s*(less|add)\s*:\s*/i, '').trim() || 'Expense';
}

/** True when an expense label already covers aged care, so we must not add `annualCareCost` on top. */
function isAgedCareLabel(label: string): boolean {
    const l = label.toLowerCase();
    return l.includes('aged care') || l.includes('care cost');
}

/** A year's label pair: the axis tick, and the longer name the tooltip and accessible label use. */
export function labelsForYear(row: { fyLabel?: string }, index: number): { label: string; fullLabel: string } {
    return {
        label: row.fyLabel || `Year ${index + 1}`,
        fullLabel: row.fyLabel ? (index === 0 ? `Comparison Yr1 (${row.fyLabel})` : row.fyLabel) : `Year ${index + 1}`,
    };
}

/**
 * Build the cashflow chart's per-year points.
 *
 * `agedCareFallbackAnnual` is the calculator's own annual care cost, used only for rows whose
 * `annualCareCost` is missing (mirrors the modal table's `annualCareCosts` fallback).
 */
export function buildCashflowChartYears(projections: ProjectionRowLike[], agedCareFallbackAnnual = 0): ChartPoint[] {
    return (projections || []).map((year, index) => {
        const income = EMPTY_INCOME();
        const expenseLines: ChartValueLine[] = [];
        const breakdown = Array.isArray(year.cashFlowBreakdown) ? (year.cashFlowBreakdown as { label?: unknown; value?: unknown }[]) : null;
        const annualCareCost = year.annualCareCost || agedCareFallbackAnnual || 0;

        // Only numeric-valued breakdowns are usable here — the suggestion engine hands some callers a
        // pre-formatted (string-valued) breakdown, which the scalar fallback below handles instead.
        // `note: true` rows explain the figures without being one of them (reinvested dividends, for
        // example, which are taxed but never banked). Summing one would invent income the client
        // never receives, so they are dropped before anything adds them up.
        const numericLines = breakdown
            ? breakdown.filter((item): item is { label: string; value: number; series?: string } =>
                typeof item?.label === 'string' && typeof item?.value === 'number' && Number.isFinite(item.value)
                && (item as { note?: unknown }).note !== true)
            : [];

        if (numericLines.length > 0) {
            // Salary is inside the breakdown's "Other Income (Cash)" line; peel it off into its own
            // series so a working client sees salary rather than an opaque "other" block.
            let salaryToAllocate = Math.max(0, year.salaryIncome || 0);
            numericLines.forEach(({ label, value, series: tag }) => {
                if (value > 0) {
                    const series = seriesForIncomeLabel(label, tag);
                    if (series === 'other' && salaryToAllocate > 0) {
                        const salaryPart = Math.min(salaryToAllocate, value);
                        salaryToAllocate -= salaryPart;
                        income.salary += salaryPart;
                        income.other += value - salaryPart;
                    } else {
                        income[series] += value;
                    }
                } else if (value < 0) {
                    expenseLines.push({ label: cleanExpenseLabel(label), value: -value });
                }
            });
        } else {
            // Cash deposited, not the opening rate — this bar is money arriving in the year. They part
            // company whenever the entitlement moves mid-year (a RAD paid on entry, a birthday reaching
            // pension age); see the engine row's `agePensionPaidForPeriod`.
            income.pension = year.agePensionPaidForPeriod ?? year.socialSecurity ?? 0;
            income.abp = year.abpIncome || 0;
            income.investment = Math.max(0, (year.investmentIncome || 0) - (year.abpIncome || 0));
            income.salary = Math.max(0, year.salaryIncome || 0);
            income.other = year.rentalIncome || 0;
            const livingExpenses = year.livingExpenses || 0;
            const tax = year.tax || 0;
            const capitalExpenses = year.capitalExpenses || 0;
            if (livingExpenses > 0) expenseLines.push({ label: 'Living Expenses', value: livingExpenses });
            if (tax > 0) expenseLines.push({ label: 'Tax', value: tax });
            if (capitalExpenses > 0) expenseLines.push({ label: 'Capital Expenses', value: capitalExpenses });
        }

        if (annualCareCost > 0 && !expenseLines.some((line) => isAgedCareLabel(line.label))) {
            expenseLines.push({ label: 'Aged Care Costs', value: annualCareCost });
        }

        const expenses = expenseLines.reduce((sum, line) => sum + line.value, 0);
        return {
            ...labelsForYear(year, index),
            values: income,
            reference: expenses,
            sections: [{ title: 'Expenses', lines: expenseLines, total: { label: 'Total expenses', value: expenses } }],
        };
    });
}
